// @flow
import * as React from 'react';
import moment from 'moment-timezone';
import { browserHistory } from 'react-router';
import { Scrollbars } from 'react-custom-scrollbars-2';
import length from 'ramda/es/length';

import getScrollbarWidth from 'app/common/lib/getScrollbarWidth';
import SrollByDrag from 'app/common/components/ScrollByDrag';
import CSSSticky from 'app/common/ui-components/css-sticky';

import enhanceChildren from './enhanceChildren';
import Datebar from './DateBar';
import Empty from '../Empty';
import Header from './Header';
import CardsContent from './CardsContent';

import styles from './Children.scss';


type Params = {
  date?: string,
}

type Props = {
  params: Params,
  students: Object,
  isStudent: boolean,
}

class Children extends React.Component<Props> {
  componentDidUpdate(prevProps: Props) {
    const { students: { keys } } = this.props;
    const curentStudentsNum = length(keys);
    const prevStudentsNum = length(prevProps.students.keys);
    if (prevStudentsNum !== curentStudentsNum) {
      const sbw = getScrollbarWidth();
      if (this.content && curentStudentsNum > 3) {
        this.content.style.marginBottom = `-${sbw}px`;
      }
    }
  }

  handleChangeDay = (day: string) => {
    browserHistory.push(`/children/${moment(day).format('YYYY-MM-DD')}`);
  };

  handleScroll = (event: SyntheticEvent<HTMLElement>) => {
    const { scrollLeft } = event.currentTarget;

    if (this.headers) {
      this.headers.scrollLeft = scrollLeft;
    }
    if (this.content) {
      this.content.scrollLeft = scrollLeft;
    }
    if (this.scrolling) {
      this.scrolling.scrollLeft(scrollLeft);
    }
  };

  handleScrollFrame = (params: Object) => {
    const { scrollLeft } = params;
    if (this.headers) {
      this.headers.scrollLeft = scrollLeft;
    }
    if (this.content) {
      this.content.scrollLeft = scrollLeft;
    }
  };

  renderHeaders = () => {
    const { params: { date }, students } = this.props;
    return students.keys.map(id => (
      <Header
        id={id}
        key={id}
        date={date}
        shadowClassName={styles.headerShadow}
      />
    ));
  };

  renderCardsSpace = () => {
    const { students: { keys } } = this.props;

    return keys.map(id =>
      <div key={id} className={styles.empty} />);
  };

  refContent = (el: React.ElementRef<'div'> | null) => {
    const { students: { keys } } = this.props;
    const studentsNum = length(keys);
    this.content = el;
    const sbw = getScrollbarWidth();
    if (this.content && studentsNum > 3) {
      this.content.style.marginBottom = `-${sbw}px`;
    }
  };

  refHeaders = (el: React.ElementRef<'div'> | null) => {
    this.headers = el;
    const sbw = getScrollbarWidth();
    if (this.headers) {
      this.headers.style.marginBottom = `-${sbw}px`;
    }
  };

  refScroll = (el: React.ElementRef<'div'> | null) => {
    this.scrolling = el;
  };

  content: React.ElementRef<'div'> | null;

  headers: React.ElementRef<'div'> | null;

  scrolling: React.ElementRef<Scrollbars> | null;

  render() {
    const {
      params: { date = moment().format('YYYY-MM-DD') },
      students,
      isStudent,
    } = this.props;

    const day = moment(date, 'YYYY-MM-DD');
    const { keys, didInvalidate } = students;
    const isEmpty = (!keys || !keys.length) && !didInvalidate;

    return (
      <div className={styles.container}>
        <CSSSticky
          number={1}
          shadowClassName={styles.headerGroup}
          className={styles.sticky}
        >

          {/* Выбор даты */}
          {keys && keys.length
            ? <Datebar day={day.clone()} onChangeDay={this.handleChangeDay} />
            : null}

          {/* <Empty /> если пусто */}
          {isEmpty && <Empty />}

          {/* Заголовки карточек */}
          <div className={styles.headers}>
            <SrollByDrag reference={this.refHeaders}>
              {reference => (
                <div className={styles.headersScrolled} ref={reference} onScroll={this.handleScroll}>
                  <div className={styles.headersRow}>
                    {this.renderHeaders()}
                  </div>
                </div>)}
            </SrollByDrag>
          </div>
        </CSSSticky>

        {/* Карточки */}
        <div className={styles.content}>
          <div className={styles.cropper}>
            <SrollByDrag reference={this.refContent}>
              {reference => (
                <div className={styles.contentScrolled} ref={reference} onScroll={this.handleScroll}>
                  <div className={styles.row}>
                    <CardsContent
                      date={date}
                      students={students}
                      isCurrentUserStudent={isStudent}
                    />
                  </div>
                </div>)}
            </SrollByDrag>
          </div>

          {/* Скролинг отдельно, потому что Scrollbars onScroll сломан */}
          {/* (в хроме не всегда возвращает конечное значение скрола) */}
          <CSSSticky
            number={-1}
          >
            <Scrollbars
              style={{ width: '100%', height: 10 }}
              onScrollFrame={this.handleScrollFrame}
              ref={this.refScroll}
            >
              <div className={styles.row}>
                {this.renderCardsSpace()}
              </div>
            </Scrollbars>
          </CSSSticky>
        </div>
      </div>
    );
  }
}

export default enhanceChildren(Children);
